// Order-intent detection for the chatbot CTA card.
//
// The chat CTA (ChatOrderCTA) only makes sense when the customer is
// *trying to buy* something — not when they are browsing, asking about
// stock for a category, or asking general questions. The bot already
// uses the RAG list to enumerate products for browse queries, and
// throwing an "In den Warenkorb" card under every product link
// (including list responses with 13 items) is noisy and misleading.
//
// This helper is a small heuristic — not an LLM call. The cost matters
// because it runs in the render loop for every assistant message.
// DE + EN are both covered; the patterns are conservative so they
// match what customers actually type, not what grammar textbooks say.
//
// Positive signals (returns true):
//   - Any number, possibly followed by "x" / "Stück" / "pieces" / etc.
//     ("5x", "100", "10 Stück", "5 pieces"). Numbers are the
//     strongest "I want a quantity" signal.
//   - Order verbs / phrases ("bestellen", "kaufen", "möchte", "order",
//     "buy", "would like", "I want", "I'd like", "hätte gerne",
//     "in den Warenkorb", "add to cart").
//
// Negative (returns false):
//   - Empty / whitespace-only input.
//   - Single category words ("Stecklinge", "Samen", "Dünger").
//   - Open questions ("Was habt ihr?", "Welche Sorten?", "Difference
//     between X and Y?").
//
// Keep this list in sync with what the system prompt recognises as
// "order intent" — see docs/chatbot-sprite/19.

const NUMBER_RE = /\b\d+\s*(?:x|st(?:ü|ue|u)ck|stk\.?|pieces?|units?|mal)?\b/i

const ORDER_VERB_RE = new RegExp(
  [
    // DE
    '\\bbestell\\w*',
    '\\bkauf\\w*',
    '\\bmöchte\\b',
    '\\bich will\\b',
    '\\bhätte gerne\\b',
    '\\breservier\\w*',
    '\\bin den warenkorb\\b',
    '\\banzahl\\b',
    '\\bmenge\\b',
    // EN
    '\\border\\w*',
    '\\bbuy\\b',
    '\\bbuys\\b',
    '\\bbought\\b',
    '\\bpurchas\\w*',
    "\\bi'?d like\\b",
    '\\bi want\\b',
    '\\bwould like\\b',
    '\\badd to cart\\b',
    '\\bquantity\\b',
    '\\bqty\\b',
  ].join('|'),
  'i',
)

export function isOrderIntent(text: string | null | undefined): boolean {
  if (typeof text !== 'string') return false
  const t = text.trim()
  if (t.length === 0) return false
  if (NUMBER_RE.test(t)) return true
  if (ORDER_VERB_RE.test(t)) return true
  return false
}
